import React, { useState, useEffect } from 'react';
import Panel from '../../common/layout/Panel';
import FlowBuilder from './FlowBuilder';
import './automations.css';
import { create, getOne, update } from '../../common/v4/BackendProvider';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useParams } from 'react-router-dom';

const AutomationsEdit = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(true);
    const [automation, setAutomation] = useState({
        id: id,
        name: 'Loading...',
        steps: [{
            id: 'trigger_1',
            type: 'trigger',
            title: 'Trigger',
            config: {
                triggerType: null
            },
            status: 'pending'
        }]
    });

    useEffect(() => {
        const loadAutomation = async () => {
            try {
                setLoading(true);
                const response = await getOne('/admin/automations/', id);
                if (response.data?.result === 'OK') {
                    const data = response.data.data;
                    
                    // Reconstruct the automation state from database
                    const steps = [];
                    
                    // Add trigger step
                    if (data.trigger_type) {
                        steps.push({
                            id: 'trigger_1',
                            type: 'trigger',
                            title: 'Trigger',
                            config: data.trigger_config || { triggerType: data.trigger_type },
                            status: 'pending'
                        });
                    }
                    
                    // Add action steps
                    if (data.steps && Array.isArray(data.steps)) {
                        data.steps.forEach((step, index) => {
                            steps.push({
                                ...step,
                                id: step.id || `step_${index + 2}`
                            });
                        });
                    }
                    
                    setAutomation({
                        id: data.id,
                        name: data.name,
                        description: data.description,
                        steps: steps
                    });
                } else {
                    toast.error('Failed to load automation');
                    navigate('/automations');
                }
            } catch (error) {
                console.error('Error loading automation:', error);
                toast.error('Failed to load automation');
                navigate('/automations');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            loadAutomation();
        }
    }, [id, navigate]);

    const isTriggerConfigured = () => {
        const trigger = automation.steps?.find(step => step.type === 'trigger');
        if (!trigger || !trigger.config?.triggerType) {
            return false;
        }

        // For specific_date_time trigger, check if all required fields are filled
        if (trigger.config.triggerType === 'specific_date_time') {
            return trigger.config.date && trigger.config.time && trigger.config.timezone;
        }

        return true;
    };

    const handleSave = async () => {
        if (!isTriggerConfigured()) {
            toast.error('Please configure the trigger before saving');
            return;
        }

        try {
            setSaving(true);
            const payload = {
                name: automation.name || 'New Automation',
                description: automation.description || '',
                trigger_type: automation.steps?.[0]?.config?.triggerType || null,
                trigger_config: automation.steps?.[0]?.config || null,
                steps: automation.steps?.filter(s => s.type !== 'trigger') || [],
                status: true,
            };
            
            const res = await update('/admin/automations/', automation.id, { data: payload });
            if (res.data?.result === 'OK') {
                toast.success('Automation updated');
                setTimeout(() => navigate('/automations'), 800);
            } else {
                toast.error('Failed to update automation');
            }
        } catch (e) {
            console.error('Failed to update automation', e);
            toast.error('Failed to update automation');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="automations-container">
                <div className="automations-header">
                    <div className="automations-title">
                        <h2>Loading automation...</h2>
                    </div>
                </div>
                <Panel title="Flow Builder">
                    <div className="text-center p-4">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                    </div>
                </Panel>
            </div>
        );
    }

    return (
        <div className="automations-container">
            <ToastContainer position="top-right" autoClose={4000} />
            <div className="automations-header">
                <div className="automations-title">
                    <h2>{automation.name}</h2>
                </div>
                <div className="automations-actions">
                    <button 
                        className="btn btn-secondary me-2"
                        onClick={() => navigate('/automations')}
                    >
                        Cancel
                    </button>
                    <button 
                        className={`btn ${isTriggerConfigured() ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={handleSave}
                        disabled={saving || !isTriggerConfigured()}
                        title={!isTriggerConfigured() ? 'Configure trigger to enable saving' : ''}
                    >
                        {saving ? 'Saving...' : 'Update Automation'}
                    </button>
                </div>
            </div>
            
            <Panel title="Flow Builder">
                <FlowBuilder 
                    automation={automation}
                    setAutomation={setAutomation}
                />
            </Panel>
        </div>
    );
};

export default AutomationsEdit; 